import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";
import { EventrDate, getV2ValueWP, spError } from "@eventr/common";
import { ColoursObj, Typography } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useLongPress } from "use-long-press";
import { useShallow } from "zustand/react/shallow";
import i18n from "../../i18n/i18next";
import { useLanguageContext } from "../../state/languageState/languageContext";
import { defaultAddSnack } from "../../state/overlayState/overlayContext";
import { useUserPrefsStore } from "../../state/userPrefsState/userPrefs.store";
import { useFormatValue } from "../hooks/useFormatValue";

export type TextTypography = "bodyRegular" | "bodyBold" | "bodySubtext" | "bodyBoldUnderlined";
interface ITextProps {
  id?: string;
  data: any;
  renderOptions: {
    value: string;
    alternate?: string;
    translatable?: boolean;
    transType?: string;
    transContext?: string;
    extraValues?: string[];
    typography?: TextTypography;
    whiteSpace?: "normal" | "nowrap";
    darkMode?: boolean;
    overflow?: string;
    text_overflow?: string;
    fontSize?: string;
    width?: string;
    minWidth?: string;
    highlight?: boolean;
    align?: "right" | "left" | "center" | "inherit" | "justify";
    formatter?: "date" | "time" | "time2" | "timeDifference" | "nightDifference" | "datetime" | "webLink" | "userOrTag";
  };
  past?: boolean;
}

export default function Text({ id, data, renderOptions, past }: ITextProps) {
  const { formatValue } = useFormatValue();
  // `formatTranslation` runs during render (below), so read the preference here.
  const showDestTime = useUserPrefsStore((s) => s.showDestTime);
  const { timeFormat } = useLanguageContext(
    useShallow((s) => ({
      timeFormat: s.timeFormat,
    })),
  );

  const content = () => {
    try {
      return formatValue(
        getV2ValueWP(renderOptions.value, data) || getFirstValidAlternateValue(renderOptions.alternate, data),
        renderOptions.formatter ?? "",
        renderOptions.extraValues?.map((v) => getV2ValueWP(v, data)),
      );
    } catch {
      return "";
    }
  };

  const contentConst: string = content();

  const longpressBind = useLongPress(
    async () => {
      if (contentConst && contentConst !== "") {
        try {
          if (Capacitor.isNativePlatform()) {
            Clipboard.write({
              string: String(contentConst),
            });
            if (Capacitor.getPlatform() === "ios") {
              if (Capacitor.getPlatform() === "ios") {
                defaultAddSnack(i18n.t("snack.copied-clipboard"), "success");
              } else {
                /// android....
              }
            } else {
              /// android....
            }
          } else {
            if (navigator.clipboard) {
              await navigator.clipboard.writeText(String(contentConst));
            } else {
              defaultAddSnack(i18n.t("snack.not-secure-context"), "error");
            }
          }
        } catch (error) {
          spError(error);
        }
      }
    },
    {
      threshold: 700, // In milliseconds
    },
  );

  function getFirstValidAlternateValue(alternate: any, data: any) {
    if (!alternate || !Array.isArray(alternate)) {
      return getV2ValueWP(alternate, data);
    }

    for (const alt of alternate) {
      const value = getV2ValueWP(alt, data);
      if (value) {
        return value;
      }
    }
    return undefined;
  }

  function formatTranslation(text: string) {
    if (renderOptions.transType) {
      switch (renderOptions.transType) {
        case "labelWithDate":
          const values = text.split(" ");

          /// Fallback to reformat time as will be in wrong format
          try {
            const timeForEventrDate = new EventrDate(values[1]);

            const timeToUse =
              showDestTime === "itinLocal"
                ? timeForEventrDate.itinLocalLuxon.toFormat(timeFormat)
                : timeForEventrDate.userLocalLuxon.toFormat(timeFormat);

            return i18n.t("cards.label", {
              label: i18n.t(`cards.${values[0]!.replace(" ", "-").toLowerCase()}`, {
                context: renderOptions.transContext,
              }),
              time: timeToUse,
            });
          } catch {
            return i18n.t("cards.label", {
              label: i18n.t(`cards.${values[0]!.replace(" ", "-").toLowerCase()}`, {
                context: renderOptions.transContext,
              }),
              time: values[1],
            });
          }

        case "altOnly":
          if (renderOptions.alternate && text === renderOptions.alternate.slice(5)) {
            return i18n.t(`cards.${text.replace(/ & /g, "-&-").replace(" ", "-").toLowerCase()}`, {
              context: renderOptions.transContext,
            });
          } else {
            return text;
          }
      }
    }

    return i18n.t(`cards.${text.replace(/ & /g, "-&-").replace(" ", "-").toLowerCase()}`, {
      context: renderOptions.transContext,
    });
  }

  return (
    <>
      {renderOptions.value && data && (
        <Box
          {...longpressBind()}
          sx={{
            maxWidth: renderOptions.width,
            minWidth: renderOptions.minWidth,
            overflow: renderOptions.overflow ?? "visible",
            textOverflow: renderOptions.text_overflow
          }}>
          <Typography
            id={id + contentConst}
            style={{ wordBreak: "break-word" }}
            variant={renderOptions.typography ?? "bodyRegular"}
            color={
              past
                ? ColoursObj.shades.shade2
                : renderOptions.highlight && getV2ValueWP("live", data)
                  ? ColoursObj.core.secondary
                  : ColoursObj.font.primary
            }
            align={renderOptions.align ?? "left"}
            sx={{
              whiteSpace: renderOptions.whiteSpace ?? "pre-line",
              fontSize: renderOptions.fontSize
            }}>
            {renderOptions.translatable ? formatTranslation(contentConst) : contentConst}
          </Typography>
        </Box>
      )}
    </>
  );
}
